import React, { forwardRef } from "react";
import clsx from "clsx";

const Select = forwardRef(({
  label,
  error,
  helperText,
  fullWidth = true,
  className = "",
  selectClassName = "",
  disabled = false,
  required = false,
  options = [],
  placeholder,
  id,
  children,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={clsx(fullWidth ? "w-full" : "inline-block", className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1"
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          className={clsx(
            "w-full px-3 py-2 text-sm border rounded-lg appearance-none",
            "bg-white dark:bg-dark-800",
            "text-dark-900 dark:text-dark-100",
            "transition-colors duration-200",
            "disabled:bg-dark-100 disabled:dark:bg-dark-700 disabled:cursor-not-allowed",
            "focus:outline-none focus:ring-2 focus:ring-offset-0",
            "pr-10",
            error
              ? "border-danger-500 focus:border-danger-500 focus:ring-danger-500/20"
              : "border-dark-300 dark:border-dark-600 focus:border-primary-500 focus:ring-primary-500/20",
            selectClassName
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.length > 0
            ? options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))
            : children}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-dark-400">
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
      {(error || helperText) && (
        <p
          className={clsx(
            "mt-1 text-xs",
            error ? "text-danger-500" : "text-dark-500 dark:text-dark-400"
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = "Select";

export default Select;
